// Facebook Graph API config
const FACEBOOK_PAGE_ID = import.meta.env.VITE_FACEBOOK_PAGE_ID || '';
const FACEBOOK_ACCESS_TOKEN = import.meta.env.VITE_FACEBOOK_ACCESS_TOKEN || '';
const FACEBOOK_GRAPH_BASE = 'https://graph.facebook.com/v25.0';

/**
 * Fields for the /posts request - all media and text included in one call
 */
const FB_POST_FIELDS = [
  'id',
  'message',
  'story',
  'created_time',
  'permalink_url',
  'full_picture',
  'attachments{media_type,media{image{src,height,width},source}}',
];

/**
 * Extract media info from the /posts attachments structure.
 * Only sets type=image/video when an actual URL is found.
 */
function extractMedia(attachments: any[]): {
  type: 'image' | 'video' | 'story';
  thumbnailUrl: string;
  fullImageUrl: string;
  videoUrl: string;
} {
  let type: 'image' | 'video' | 'story' = 'story';
  let thumbnailUrl = '';
  let fullImageUrl = '';
  let videoUrl = '';

  if (!attachments || attachments.length === 0) {
    return { type: 'story', thumbnailUrl: '', fullImageUrl: '', videoUrl: '' };
  }

  const videoTypeSet = new Set(['video', 'video_autoplayed']);

  for (const att of attachments) {
    const mediaType = att.media_type || '';
    const imgSrc = att.media?.image?.src || '';
    const src = att.media?.source || '';

    // Video types
    if (videoTypeSet.has(mediaType)) {
      type = 'video';
      if (src) videoUrl = src;
      if (imgSrc) {
        if (!thumbnailUrl) thumbnailUrl = imgSrc;
        if (!fullImageUrl) fullImageUrl = imgSrc;
      }
    }
    // Photo / Album / Cover types
    else if (['photo', 'photo_album', 'album', 'cover', 'added_photos'].includes(mediaType)) {
      if (type !== 'video') type = 'image';
      const url = imgSrc || src;
      if (url) {
        if (!fullImageUrl) fullImageUrl = url;
        if (!thumbnailUrl) thumbnailUrl = url;
      }
    }
    // Link / Share types (shared links with preview image)
    else if (['link', 'share', 'shared_story', 'status', 'offer'].includes(mediaType)) {
      // Only set image type if we actually get a URL
      if (imgSrc && type === 'story') {
        type = 'image';
        fullImageUrl = imgSrc;
        thumbnailUrl = imgSrc;
      }
    }
  }

  return { type, thumbnailUrl, fullImageUrl, videoUrl };
}

/**
 * Fetch engagement counts (likes, comments) + up to 20 recent comments for a post
 */
async function fetchPostEngagement(postId: string): Promise<{
  likes: number;
  commentCount: number;
  comments: SocialComment[];
}> {
  let likes = 0;
  let commentCount = 0;
  const comments: SocialComment[] = [];

  // Ensure post ID is in the correct format (page-id_post-id)
  let fullPostId = postId;
  if (!postId.includes('_')) {
    fullPostId = `${FACEBOOK_PAGE_ID}_${postId}`;
  }

  const [likesResp, commentsResp] = await Promise.allSettled([
    (async () => {
      const likesUrl = new URL(`${FACEBOOK_GRAPH_BASE}/${fullPostId}/likes`);
      likesUrl.searchParams.set('summary', '1');
      likesUrl.searchParams.set('limit', '0');
      likesUrl.searchParams.set('access_token', FACEBOOK_ACCESS_TOKEN);
      return fetch(likesUrl.toString(), { signal: AbortSignal.timeout(10000) });
    })(),
    (async () => {
      const commentsUrl = new URL(`${FACEBOOK_GRAPH_BASE}/${fullPostId}/comments`);
      commentsUrl.searchParams.set('fields', 'from{id,name},message,created_time,like_count');
      commentsUrl.searchParams.set('summary', '1');
      commentsUrl.searchParams.set('limit', '20');
      commentsUrl.searchParams.set('access_token', FACEBOOK_ACCESS_TOKEN);
      return fetch(commentsUrl.toString(), { signal: AbortSignal.timeout(10000) });
    })(),
  ]);

  try {
    if (likesResp.status === 'fulfilled' && likesResp.value.ok) {
      const data = await likesResp.value.json();
      likes = data.summary?.total_count ?? 0;
    }
  } catch {}

  try {
    if (commentsResp.status === 'fulfilled' && commentsResp.value.ok) {
      const data = await commentsResp.value.json();
      commentCount = data.summary?.total_count ?? 0;
      for (const c of data.data ?? []) {
        const commenter = c.from;
        let authorName: string;
        let userId: string;

        if (commenter && commenter.id) {
          userId = commenter.id;
          authorName = commenter.name || commenter.id || 'Facebook User';
        } else {
          authorName = 'Facebook User';
          userId = '';
        }
        // Construct Facebook avatar URL from user ID
        const avatarUrl = userId
          ? `https://graph.facebook.com/${userId}/picture?type=square&access_token=${FACEBOOK_ACCESS_TOKEN}`
          : '';

        comments.push({
          id: `fb-comment-${c.id}`,
          author: authorName,
          avatar: avatarUrl,
          text: c.message || '',
          date: c.created_time ? c.created_time.split('T')[0] : '',
          likes: c.like_count ?? 0,
        });
      }
    }
  } catch {}

  return { likes, commentCount, comments };
}

// ============================================================
// Build & fetch pipeline
// ============================================================

/**
 * Combine feed data + engagement into a SocialMediaPost
 */
function buildFacebookPost(
  raw: Record<string, any>,
  engagement: { likes: number; commentCount: number; comments: SocialComment[] },
): SocialMediaPost {
  const textContent = raw.message || raw.story || 'Facebook Post';
  const title = textContent.length > 80 ? textContent.substring(0, 80) + '…' : textContent;

  // Extract media from attachments
  const attachments = raw.attachments?.data ?? [];
  const media = extractMedia(attachments);

  // Fallback 1: top-level full_picture
  let thumbnailUrl = media.thumbnailUrl;
  let type = media.type;

  if (!thumbnailUrl && raw.full_picture) {
    thumbnailUrl = raw.full_picture;
    if (type === 'story') type = 'image';
  }

  // If still no image but type says image, downgrade to story
  if (type === 'image' && !thumbnailUrl) {
    type = 'story';
  }

  return {
    id: `fb-${raw.id}`,
    platform: 'facebook',
    type,
    thumbnailUrl,
    title,
    description: textContent.substring(0, 150),
    content: textContent,
    author: 'Dr. Rui – Official Page',
    date: raw.created_time ? raw.created_time.split('T')[0] : new Date().toISOString().split('T')[0],
    stats: {
      views: 0,
      likes: engagement.likes || 0,
      comments: engagement.commentCount || 0,
      shares: 0,
    },
    comments: engagement.comments,
    platformUrl: raw.permalink_url || `https://www.facebook.com/${raw.id}`,
    featured: engagement.likes > 50 || engagement.commentCount > 20,
    cached: false,
  };
}

/**
 * Fetch real Facebook Page posts directly from the Graph API
 *
 * Pipeline:
 * 1. GET /{page_id}/feed → all posts with images, videos, text, attachments
 * 2. For each post, fetch engagement (likes count + comments)
 * 3. Combine into SocialMediaPost objects
 */
async function fetchFacebookPosts(): Promise<SocialMediaPost[]> {
  if (!FACEBOOK_PAGE_ID || !FACEBOOK_ACCESS_TOKEN) {
    console.warn('Facebook credentials not configured');
    return [];
  }

  try {
    // Step 1: Fetch posts with ALL data (images, videos, text)
    const postsUrl = new URL(`${FACEBOOK_GRAPH_BASE}/${FACEBOOK_PAGE_ID}/posts`);
    postsUrl.searchParams.set('fields', FB_POST_FIELDS.join(','));
    postsUrl.searchParams.set('limit', '25');
    postsUrl.searchParams.set('access_token', FACEBOOK_ACCESS_TOKEN);

    const postsResp = await fetch(postsUrl.toString(), {
      signal: AbortSignal.timeout(15000),
    });

    if (!postsResp.ok) {
      const err = await postsResp.text();
      console.error('Facebook Posts error:', postsResp.status, err);
      return [];
    }

    const postsData = await postsResp.json();
    const rawPosts: Record<string, any>[] = postsData.data || [];

    if (rawPosts.length === 0) {
      return [];
    }

    // Step 2: Fetch engagement (likes + comments) for each post
    const results = await Promise.allSettled(
      rawPosts.map(async (post) => {
        const engagement = await fetchPostEngagement(post.id);
        return buildFacebookPost(post, engagement);
      }),
    );

    const posts: SocialMediaPost[] = [];
    for (const r of results) {
      if (r.status === 'fulfilled') posts.push(r.value);
    }

    return posts;
  } catch (error) {
    console.error('Error fetching Facebook posts:', error);
    return [];
  }
}

/**
 * Check if Facebook API is reachable
 */
async function checkFacebookApiHealth(): Promise<boolean> {
  if (!FACEBOOK_PAGE_ID || !FACEBOOK_ACCESS_TOKEN) return false;

  try {
    const url = new URL(`${FACEBOOK_GRAPH_BASE}/${FACEBOOK_PAGE_ID}`);
    url.searchParams.set('fields', 'id,name');
    url.searchParams.set('access_token', FACEBOOK_ACCESS_TOKEN);

    const response = await fetch(url.toString(), {
      signal: AbortSignal.timeout(10000),
    });

    return response.ok;
  } catch {
    return false;
  }
}

export interface SocialMediaPost {
  id: string;
  platform: 'facebook' | 'youtube' | 'instagram' | 'tiktok';
  type: 'image' | 'video' | 'story';
  thumbnailUrl: string;
  title: string;
  description: string;
  content: string;
  author: string;
  date: string;
  stats: {
    views: number;
    likes: number;
    comments: number;
    shares: number;
  };
  comments: SocialComment[];
  platformUrl: string;
  featured?: boolean;
  cached?: boolean;
  /** Raw Facebook post ID for API calls */
  rawPlatformId?: string;
}

export interface SocialComment {
  id: string;
  author: string;
  avatar: string;
  text: string;
  date: string;
  likes: number;
}

export interface PlatformHealthStatus {
  status: string;
  timestamp: string;
  platforms: {
    facebook: boolean;
    youtube: boolean;
    instagram: boolean;
    tiktok: boolean;
  };
}

/**
 * Fetch all posts from all platforms
 * Currently only Facebook is live; others fall back to mock data.
 */
export async function fetchAllSocialMediaPosts(): Promise<SocialMediaPost[]> {
  const posts = await fetchFacebookPosts();

  // Sort by date (newest first)
  posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return posts;
}

/**
 * Keep backward-compat for any code that calls fetchSocialMediaPosts by platform
 */
export async function fetchSocialMediaPosts(platform?: string): Promise<SocialMediaPost[]> {
  if (platform === 'facebook') return fetchFacebookPosts();

  const all = await fetchAllSocialMediaPosts();
  return platform ? all.filter((p) => p.platform === platform) : all;
}

/**
 * Check API health status (client-side, no server)
 */
export async function checkApiHealth(): Promise<PlatformHealthStatus | null> {
  const fbHealthy = await checkFacebookApiHealth();

  return {
    status: fbHealthy ? 'connected' : 'fallback',
    timestamp: new Date().toISOString(),
    platforms: {
      facebook: fbHealthy,
      youtube: false,
      instagram: false,
      tiktok: false,
    },
  };
}

/**
 * Fetch comments for a specific post – Facebook only (via Graph API)
 * Includes author name and profile photo
 */
export async function fetchPostComments(
  postId: string,
  platform: string,
): Promise<SocialComment[]> {
  if (platform !== 'facebook' || !FACEBOOK_PAGE_ID || !FACEBOOK_ACCESS_TOKEN) return [];

  try {
    // postId is "fb-<fb_post_id>" → strip the prefix for the real FB ID
    let fbId = postId.replace('fb-', '');

    // Facebook Graph API requires the full post ID format: {page-id}_{post-id}
    // If the ID doesn't contain an underscore, we need to prepend the page ID
    if (!fbId.includes('_')) {
      fbId = `${FACEBOOK_PAGE_ID}_${fbId}`;
    }

    // Use URL and searchParams for proper encoding
    const url = new URL(`${FACEBOOK_GRAPH_BASE}/${fbId}/comments`);
    url.searchParams.set('fields', 'from{id,name},message,created_time,like_count');
    url.searchParams.set('limit', '50');
    url.searchParams.set('access_token', FACEBOOK_ACCESS_TOKEN);

    const response = await fetch(url.toString(), {
      signal: AbortSignal.timeout(10000),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[SocialMedia] Facebook comments API error:', response.status, errorText);
      return [];
    }

    const data = await response.json();

    const fetchedComments = (data.data || []).map((c: any, index: number) => {
      // Handle cases where 'from' is null (deleted accounts, unavailable profiles, etc.)
      const commenter = c.from;
      let authorName: string;
      let userId: string;

      if (commenter && commenter.id) {
        userId = commenter.id;
        authorName = commenter.name || commenter.id || `Facebook User ${index + 1}`;
      } else {
        // Fallback for anonymous/unavailable users
        authorName = `Facebook User`;
        userId = '';
      }

      // Construct Facebook avatar URL from user ID (no token needed for public profile pics)
      const avatarUrl = userId
        ? `https://graph.facebook.com/${userId}/picture?type=normal`
        : '';

      return {
        id: `fb-comment-${c.id}`,
        author: authorName,
        avatar: avatarUrl,
        text: c.message || '',
        date: c.created_time ? c.created_time.split('T')[0] : '',
        likes: c.like_count ?? 0,
      };
    });

    return fetchedComments;
  } catch (error) {
    console.error('[SocialMedia] Failed to fetch comments from Facebook:', error);
    return [];
  }
}

